import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiUserPlus, FiTrash2, FiSearch, FiDownload, FiMail, FiX, FiSend, FiCheckCircle } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import { getAllSubscribers, deleteSubscriber, sendNewsletter } from '../../Redux/slices/subscriberSlice';

const Subscribers = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [newsletterSent, setNewsletterSent] = useState(false);
  const [newsletterData, setNewsletterData] = useState({
    subject: '',
    message: ''
  });
  const dispatch = useDispatch();
  const { subscribers } = useSelector(state => state.subscribers);

  useEffect(() => {
    dispatch(getAllSubscribers());
  }, [dispatch]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this subscriber?')) {
      await dispatch(deleteSubscriber(id));
      dispatch(getAllSubscribers());
    }
  };

  const handleExport = () => {
    // Create CSV content
    const headers = ['Email', 'Subscribed Date', 'Status'];
    const csvContent = [
      headers.join(','),
      ...subscribers.map(sub => 
        `${sub.email},${sub.subscribedDate},${sub.status}`
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendNewsletter = async () => {
    if (!newsletterData.subject.trim() || !newsletterData.message.trim()) {
      alert('Please fill in both subject and message fields');
      return;
    }

    setNewsletterSent(true);
    await dispatch(sendNewsletter(newsletterData));
    
    setTimeout(() => {
      setShowNewsletterModal(false);
      setNewsletterSent(false);
      setNewsletterData({ subject: '', message: '' });
    }, 2000);
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="subscribers" />

      <div className="lg:ml-64">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Newsletter Subscribers</h2>
              <p className="text-sm text-gray-600">{subscribers.length} total subscribers</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">Admin User</p>
                <p className="text-xs text-gray-500">admin@houseofpraise.com</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                A
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Stats Card */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm mb-1">Total Subscribers</p>
                <p className="text-4xl font-bold text-gray-800">{subscribers.length}</p>
              </div>
              <div className="bg-purple-100 p-4 rounded-lg">
                <FiUserPlus className="text-purple-600" size={32} />
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-2">
                <button 
                  onClick={() => setShowNewsletterModal(true)}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                >
                  <FiMail size={18} />
                  <span>Send Newsletter</span>
                </button>
                <button 
                  onClick={handleExport}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition"
                >
                  <FiDownload size={18} />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>

          {/* Subscribers Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Email Address</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Subscribed Date</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Status</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber._id} className="hover:bg-gray-50">
                      <td className="py-4 px-6 text-gray-800">{subscriber.email}</td>
                      <td className="py-4 px-6 text-gray-600">{new Date(subscriber.createdAt).toLocaleDateString()}</td>
                      <td className="py-4 px-6">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                          {subscriber.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => handleDelete(subscriber._id)}
                          className="text-red-600 hover:text-red-700 font-medium"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredSubscribers.length === 0 && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center mt-6">
              <FiUserPlus className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Subscribers Found</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Try adjusting your search terms' : 'No subscribers yet'}
              </p>
            </div>
          )}
        </main>
      </div>

      {/* Newsletter Modal */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {!newsletterSent ? (
              <>
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FiMail className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Send Newsletter</h3>
                      <p className="text-sm text-gray-600">Sending to {subscribers.length} subscribers</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNewsletterModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FiX size={24} />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Subject Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newsletterData.subject}
                      onChange={(e) => setNewsletterData({ ...newsletterData, subject: e.target.value })}
                      placeholder="Enter newsletter subject..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Message Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={newsletterData.message}
                      onChange={(e) => setNewsletterData({ ...newsletterData, message: e.target.value })}
                      placeholder="Write your newsletter message here..."
                      rows="10"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      {newsletterData.message.length} characters
                    </p>
                  </div>

                  {/* Preview Box */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
                    <div className="bg-white p-4 rounded border border-gray-200">
                      <p className="font-bold text-gray-800 mb-2">{newsletterData.subject || 'Subject will appear here'}</p>
                      <p className="text-gray-600 whitespace-pre-wrap">{newsletterData.message || 'Message content will appear here'}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSendNewsletter}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                    >
                      <FiSend size={20} />
                      Send to All Subscribers
                    </button>
                    <button
                      onClick={() => setShowNewsletterModal(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                  <FiCheckCircle className="text-green-600" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Newsletter Sent!</h3>
                <p className="text-gray-600">
                  Your newsletter has been successfully sent to {subscribers.length} subscribers.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Subscribers;
